import React, { useCallback, useMemo, useRef, useState } from 'react';
import { connect } from 'dva';
import { ListView } from 'antd-mobile';
import EmptyRecord from '@/components/EmptyRecord';
import { useMounted } from '@/utils/hooks';
import RecommendPageWrapper from './components/RecommendPageWrapper';
import RecommendPicker from './components/RecommendPicker';
import RecommendChart from './components/RecommendChart';
import Tip from './components/Tip';
import {
  ISP_OPTIONS,
  ISP_REWARD_PAGE_SIZE,
  PROVINCE_FILTER_TYPE,
  ISP_FILTER_TYPE,
  ALL_FILTER_VALUE,
  ISP_OPTIONS_WITH_ALL,
  PROVINCE_OPTIONS_WITH_ALL,
  ISP_MAP,
  PROVICNE_MAP,
  TELECOM_CODE,
} from './config';
import styles from './styles/style.less';

const SORT_FUNC = (a, b) => {
  if (a['star'] === b['star']) {
    return a.region.localeCompare(b.region, 'zh-Hans-CN', { sensitivity: 'accent' });
  } else {
    if (a['star'] > b['star']) {
      return -1;
    } else {
      return 1;
    }
  }
};

const ISPRewardRecommend = ({
  recommend: { recommendInfo, displayList, chartData },
  loading,
  dispatch,
}) => {
  const dataSource = useRef(
    new ListView.DataSource({
      rowHasChanged: (r1, r2) => r1 !== r2,
    })
  );
  const [filterISP, setFilterISP] = useState(ALL_FILTER_VALUE);
  const [filterProvince, setFilterProvince] = useState(ALL_FILTER_VALUE);

  const listData = useMemo(
    () => dataSource.current.cloneWithRows(displayList || []),
    [displayList]
  );
  const getIspRecommendInfo = () => {
    dispatch({
      type: 'recommend/getIspRecommendInfo',
      payload: {
        order: 2,
        num: 0,
      },
    });
  };

  const handleIspChange = useCallback(
    (val) => {
      const ispVal = val[0];
      // console.log('运营商筛选改变了: ', ispVal, typeof ispVal);
      dispatch({
        type: 'recommend/save',
        payload: {
          chartData: recommendInfo?.filter((item) => item.ispID === ispVal) || [],
        },
      });
    },
    [recommendInfo, dispatch]
  );

  const handleFilterDisplayList = useCallback(
    (province, isp) => {
      // console.log('province, isp: ', province, isp);
      // console.log('获得展示的列表');
      if (isp === ALL_FILTER_VALUE) {
        if (province === ALL_FILTER_VALUE) {
          return recommendInfo?.sort(SORT_FUNC);
        } else {
          return recommendInfo?.filter((item) => item.provinceID === province).sort(SORT_FUNC);
        }
      } else {
        if (province === ALL_FILTER_VALUE) {
          return recommendInfo?.filter((item) => item.ispID === isp).sort(SORT_FUNC);
        } else {
          return recommendInfo
            ?.filter((item) => item.provinceID === province && item.ispID === isp)
            .sort(SORT_FUNC);
        }
      }
    },
    [recommendInfo]
  );

  const handleFilterRewardInfo = useCallback(
    (filterVal, type) => {
      // console.log('filterVal, type: ', filterVal, type);
      const realFilterVal = filterVal[0];
      let filterList = recommendInfo;
      if (type === PROVINCE_FILTER_TYPE) {
        setFilterProvince(realFilterVal);
        filterList = handleFilterDisplayList(realFilterVal, filterISP);
      } else {
        setFilterISP(realFilterVal);
        filterList = handleFilterDisplayList(filterProvince, realFilterVal);
      }
      dispatch({
        type: 'recommend/save',
        payload: {
          displayList: filterList,
        },
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [recommendInfo, filterISP, filterProvince]
  );

  const row = useCallback((item, _, rowID) => {
    const { provinceID, ispID, star } = item;
    return (
      <li className={styles.rewardItem} key={rowID}>
        <span className={styles.rewardText}>{PROVICNE_MAP[provinceID]}</span>
        <span className={`${styles.rewardText} ${styles.isp}`}>{ISP_MAP[ispID]}</span>
        <span className={styles.rewardText}>
          <i className={`${styles.stars} ${styles[`stars-${star}`]}`}></i>
        </span>
      </li>
    );
  }, []);

  useMounted(() => {
    getIspRecommendInfo();
  });

  return (
    <RecommendPageWrapper title="运营商奖励">
      <Tip text="本奖励适用产品：官方硬件系列、三方系列、容器魔方、纳思小星、赚钱宝电脑版、x86智能调度业务等" />
      <div className={`${styles.infoCard} ${styles.isp}`}>
        <h3 className={`${styles.cardTitle} ${styles.isp}`}>运营商奖励</h3>
        <p className={styles.selectText}>运营商：</p>
        <RecommendPicker
          data={ISP_OPTIONS}
          title="运营商"
          disabled={loading}
          value={[TELECOM_CODE]}
          onConfirm={handleIspChange}
          childrenClassName={styles.selectVal}
        />
      </div>
      <RecommendChart chartData={chartData} />
      <div className={`${styles.infoCard} ${styles.reward}`}>
        <h3 className={`${styles.cardTitle} ${styles.reward}`}>
          奖励明细<span className={styles.rewardNote}>星级越高奖励越高，可在每日收益明细查看</span>
        </h3>
        <div className={styles.rewardSelectorHeader}>
          <RecommendPicker
            data={PROVINCE_OPTIONS_WITH_ALL}
            title="省份"
            value={[ALL_FILTER_VALUE]}
            extra="全部"
            onConfirm={(val) => handleFilterRewardInfo(val, PROVINCE_FILTER_TYPE)}
            childrenClassName={styles.rewardSelectVal}
          />
          <RecommendPicker
            data={ISP_OPTIONS_WITH_ALL}
            title="运营商"
            value={[ALL_FILTER_VALUE]}
            extra="全部"
            onConfirm={(val) => handleFilterRewardInfo(val, ISP_FILTER_TYPE)}
            childrenClassName={`${styles.rewardSelectVal} ${styles.isp}`}
          />
          <div>
            <p className={`${styles.rewardSelectVal} ${styles.rewardStar}`}>运营商奖励星级</p>
          </div>
        </div>
        <div className={styles.rewardInfoList}>
          {displayList?.length > 0 ? (
            <ListView
              initialListSize={20}
              dataSource={listData}
              renderFooter={null}
              renderRow={row}
              pageSize={ISP_REWARD_PAGE_SIZE}
              useBodyScroll
            />
          ) : (
            <EmptyRecord text="暂时没有符合条件的筛选项~" />
          )}
        </div>
      </div>
    </RecommendPageWrapper>
  );
};
export default connect(({ recommend, loading }) => ({
  recommend,
  loading: loading.effects['recommend/getIspRecommendInfo'],
}))(ISPRewardRecommend);
